document.addEventListener('DOMContentLoaded', () => {
  // --- Configuration ---
  const netlifyFunctionUrl = 'https://orvizoho.netlify.app/.netlify/functions/catalogue';
  let formInitialized = false;
  let catalogueClickListenerAdded = false;
  let initializedForms = new Set(); // Track which forms have been initialized
  // --- End Configuration ---

  const waitForElement = (selector, timeout = 10000) => {
    return new Promise((resolve, reject) => {
      const element = document.querySelector(selector);
      if (element) {
        resolve(element);
        return;
      }

      const observer = new MutationObserver((mutations, obs) => {
        const element = document.querySelector(selector);
        if (element) {
          obs.disconnect();
          resolve(element);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      setTimeout(() => {
        observer.disconnect();
        reject(new Error(`Element ${selector} not found within ${timeout}ms`));
      }, timeout);
    });
  };

  // Setup catalogue link click handler
  const setupCatalogueLinks = () => {
    // Enhanced selectors for catalogue links
    const catalogueSelectors = [
      'a.catalogue',
      '.catalogue a',
      '[class*="catalogue"]',
      'a[href*="catalogue"]',
      'a[onclick*="catalogue"]',
      '.catalog-trigger',
      '.download-catalogue',
      '.save-copy',
      '[data-toggle*="modal"]',
      '[data-target*="catalogue"]',
      'a:contains("Save A Copy")',
      'a:contains("Download")',
      'a:contains("Catalogue")',
      'button[onclick*="catalogue"]'
    ];

    let foundLinks = [];
    catalogueSelectors.forEach(selector => {
      try {
        const links = document.querySelectorAll(selector);
        links.forEach(link => {
          if (!foundLinks.includes(link)) {
            foundLinks.push(link);
          }
        });
      } catch (e) {
        // Invalid selector, continue
      }
    });

    // Also check by text content
    const allLinks = document.querySelectorAll('a, button');
    allLinks.forEach(link => {
      const text = link.textContent.toLowerCase().trim();
      if ((text.includes('save a copy') || text.includes('download') || text.includes('catalogue') ||
        text.includes('catalog') || text.includes('brochure')) &&
        !foundLinks.includes(link)) {
        foundLinks.push(link);
      }
    });

    foundLinks.forEach((link, index) => {
      // Remove existing listeners to avoid duplicates
      link.removeEventListener('click', catalogueClickHandler);
      link.addEventListener('click', catalogueClickHandler);
    });

    if (foundLinks.length > 0) {
      catalogueClickListenerAdded = true;
    }
  };

  // Separate click handler function
  const catalogueClickHandler = (e) => {
    // Reset initialization status to allow re-initialization
    formInitialized = false;

    // Wait for popup/modal to appear and form to load
    setTimeout(() => {
      initCatalogueFormInPopup();
    }, 500);

    // Also try after longer delays in case the popup takes time
    setTimeout(() => {
      if (!formInitialized) {
        initCatalogueFormInPopup();
      }
    }, 1500);

    setTimeout(() => {
      if (!formInitialized) {
        initCatalogueFormInPopup();
      }
    }, 3000);
  };

  // Initialize catalogue form (for both static and popup forms)
  const initCatalogueFormInPopup = async () => {
    // Simple direct approach

    try {
      // Multiple selectors to find the catalogue form
      const formSelectors = [
        'form#home_catalogue_form',
        'form[id*="catalogue"]',
        'form[class*="catalogue"]',
        'form[action*="contact"]',
        '.popup form',
        '.modal form',
        '.contact-form form',
        'div[data-section-type="contact-form"] form',
        'form:has(input[name*="first_name"])',
        'form:has(input[name*="Company"])',
        'form:has(select[name*="Profession"])'
      ];

      let catalogueForm = null;

      for (const selector of formSelectors) {
        try {
          catalogueForm = await waitForElement(selector, 3000);
          if (catalogueForm) {
            break;
          }
        } catch (e) {
          // Continue to next selector
        }
      }

      if (!catalogueForm) {
        return;
      }

      // Create unique identifier for this form
      const formId = catalogueForm.id || catalogueForm.action || 'form-' + Date.now();

      // Check if this form is already handled
      if (catalogueForm.hasAttribute('data-orvi-handled')) {
        return;
      }

      // Inject City field if missing for Catalogue form
      const companyInput = catalogueForm.querySelector('input[name="contact[Company]"]');
      const cityInputExists = catalogueForm.querySelector('input[name="contact[city]"], input[name="contact[City]"]');

      if (companyInput && !cityInputExists) {
        const cityWrapper = document.createElement('div');
        const inputClass = companyInput.className;
        const parentClass = companyInput.parentElement.className;

        cityWrapper.className = parentClass || 'form-group';
        cityWrapper.innerHTML = `<input type="text" name="contact[City]" class="${inputClass}" placeholder="City" required>`;

        const companyContainer = companyInput.closest('div') || companyInput;
        companyContainer.after(cityWrapper);
      }

      // Find form fields with flexible selectors
      const fieldMappings = findCatalogueFormFields(catalogueForm);

      // Check for missing required fields with better error reporting
      const missingFields = [];
      if (!fieldMappings.firstName) missingFields.push('firstName');
      if (!fieldMappings.lastName) missingFields.push('lastName');
      if (!fieldMappings.email) missingFields.push('email');
      if (!fieldMappings.phone) missingFields.push('phone');
      if (!fieldMappings.company) missingFields.push('company');
      if (!fieldMappings.profession) missingFields.push('profession');
      if (!fieldMappings.city) missingFields.push('city');

      if (missingFields.length > 0) {
        // Try to help debug by showing available form inputs
        const availableInputs = Array.from(catalogueForm.querySelectorAll('input, select')).map(input => ({
          name: input.name,
          type: input.type,
          tagName: input.tagName,
          placeholder: input.placeholder,
          id: input.id
        }));

        // Still initialize the form but don't add submit handler
        const formId = catalogueForm.id || catalogueForm.action || 'form-' + Date.now();
        initializedForms.add(formId);
        return;
      }

      // Create a bound event handler for this specific form
      const boundHandler = (e) => handleCatalogueFormSubmit(e, fieldMappings);

      // Add event listener directly
      catalogueForm.addEventListener('submit', boundHandler);

      // Mark form as handled to prevent conflicts
      catalogueForm.setAttribute('data-orvi-handled', 'catalogue-script');
      // Mark this form as initialized
      initializedForms.add(formId);
      formInitialized = true;

    } catch (error) {
      // Failed to initialize catalogue form
    }
  };

  // Find catalogue form fields with flexible selectors
  const findCatalogueFormFields = (form) => {
    const mappings = {};

    // First Name field
    const firstNameSelectors = [
      'input[name="contact[first_name]"]',
      'input[name="first_name"]',
      'input[name="firstName"]',
      'input[name*="first"]',
      'input[placeholder*="first" i]',
      'input[id*="first"]'
    ];

    // Last Name field
    const lastNameSelectors = [
      'input[name="contact[last_name]"]',
      'input[name="last_name"]',
      'input[name="lastName"]',
      'input[name*="last"]',
      'input[placeholder*="last" i]',
      'input[id*="last"]'
    ];

    // Email field
    const emailSelectors = [
      'input[name="contact[email]"]',
      'input[name="email"]',
      'input[type="email"]',
      'input[name*="email"]',
      'input[placeholder*="email" i]',
      'input[id*="email"]'
    ];

    // Phone field
    const phoneSelectors = [
      'input[name="contact[phone]"]',
      'input[name="phone"]',
      'input[type="tel"]',
      'input[name*="phone"]',
      'input[placeholder*="phone" i]',
      'input[id*="phone"]'
    ];

    // Company field
    const companySelectors = [
      'input[name="contact[Company]"]',
      'input[name="contact[company]"]',
      'input[name="company"]',
      'input[name="Company"]',
      'input[name*="company"]',
      'input[placeholder*="company" i]',
      'input[id*="company"]'
    ];

    // Profession field (select dropdown)
    const professionSelectors = [
      'select[name="contact[Profession]"]',
      'select[name="contact[profession]"]',
      'select[name="profession"]',
      'select[name="Profession"]',
      'select[name*="profession"]',
      'select[id*="profession"]'
    ];

    // City field
    const citySelectors = [
      'input[name="contact[city]"]',
      'input[name="contact[City]"]',
      'input[name="city"]',
      'input[name="City"]',
      'input[name*="city"]',
      'input[placeholder*="city" i]',
      'input[id*="city"]'
    ];

    // Find fields
    mappings.firstName = findFieldBySelectors(form, firstNameSelectors);
    mappings.lastName = findFieldBySelectors(form, lastNameSelectors);
    mappings.email = findFieldBySelectors(form, emailSelectors);
    mappings.phone = findFieldBySelectors(form, phoneSelectors);
    mappings.company = findFieldBySelectors(form, companySelectors);
    mappings.profession = findFieldBySelectors(form, professionSelectors);
    mappings.city = findFieldBySelectors(form, citySelectors);

    return mappings;
  };

  const findFieldBySelectors = (form, selectors) => {
    for (const selector of selectors) {
      const field = form.querySelector(selector);
      if (field) {
        return field;
      }
    }
    return null;
  };

  const init = async () => {
    // Setup catalogue link handlers
    setupCatalogueLinks();

    // Also try to find static catalogue forms
    initCatalogueFormInPopup();
  };

  const handleCatalogueFormSubmit = async (event, fieldMappings) => {
    event.preventDefault();

    const form = event.target;
    const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
    const originalButtonText = submitButton ? (submitButton.innerHTML || submitButton.value) : '';

    // --- Get Form Data using field mappings ---
    if (!fieldMappings.firstName || !fieldMappings.lastName || !fieldMappings.email ||
      !fieldMappings.phone || !fieldMappings.company || !fieldMappings.profession || !fieldMappings.city) {
      return;
    }

    // Validate form data with safer extraction
    let formData;
    try {
      formData = {
        firstName: (fieldMappings.firstName?.value || '').trim(),
        lastName: (fieldMappings.lastName?.value || '').trim(),
        email: (fieldMappings.email?.value || '').trim(),
        phone: (fieldMappings.phone?.value || '').trim(),
        company: (fieldMappings.company?.value || '').trim(),
        profession: (fieldMappings.profession?.value || '').trim(),
        city: (fieldMappings.city?.value || '').trim(),
        leadSource: 'Catalogue Request',
        sourceByGroup: 'Catalogue Request'
      };
    } catch (error) {
      return;
    }

    // Enhanced validation with specific field messages
    const missingRequiredFields = [];
    if (!formData.firstName) missingRequiredFields.push('First Name');
    if (!formData.lastName) missingRequiredFields.push('Last Name');
    if (!formData.email) missingRequiredFields.push('Email');
    if (!formData.phone) missingRequiredFields.push('Phone');
    if (!formData.profession) missingRequiredFields.push('Profession');
    if (!formData.city) missingRequiredFields.push('City');

    if (missingRequiredFields.length > 0) {
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return;
    }

    // --- UI Feedback: Loading State ---
    if (submitButton) {
      submitButton.disabled = true;
      if (submitButton.tagName === 'BUTTON') {
        submitButton.innerHTML = 'Sending...';
      } else {
        submitButton.value = 'Sending...';
      }
    }

    // --- API Call ---
    try {
      const response = await fetch(netlifyFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData),
      });

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        throw new Error('Invalid response from server');
      }

      if (response.ok && result.success) {
        // --- UI Feedback: Success State ---
        if (submitButton) {
          if (submitButton.tagName === 'BUTTON') {
            submitButton.innerHTML = 'Sent!';
          } else {
            submitButton.value = 'Sent!';
          }
        }

        // Clear form
        form.reset();

        // Reset button after a delay
        setTimeout(() => {
          if (submitButton) {
            if (submitButton.tagName === 'BUTTON') {
              submitButton.innerHTML = originalButtonText;
            } else {
              submitButton.value = originalButtonText;
            }
            submitButton.disabled = false;
          }
        }, 3000);

      } else {
        // --- UI Feedback: Error State ---
        if (submitButton) {
          submitButton.disabled = false;
          if (submitButton.tagName === 'BUTTON') {
            submitButton.innerHTML = originalButtonText;
          } else {
            submitButton.value = originalButtonText;
          }
        }
      }
    } catch (error) {
      // --- UI Feedback: Network Error State ---
      if (submitButton) {
        submitButton.disabled = false;
        if (submitButton.tagName === 'BUTTON') {
          submitButton.innerHTML = originalButtonText;
        } else {
          submitButton.value = originalButtonText;
        }
      }
    }
  };

  // Initialize with multiple strategies for robustness
  init(); // Try to initialize immediately

  // Also try after a short delay in case the form loads asynchronously
  setTimeout(() => {
    if (!formInitialized) {
      init();
    }
  }, 2000);

  // Watch for dynamic content changes (e.g., modal popups, AJAX content)
  const observer = new MutationObserver((mutationsList, obs) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        // Check for any catalogue form appearing
        const hasCatalogueForm = document.querySelector('form#home_catalogue_form') ||
          document.querySelector('form[id*="catalogue"]') ||
          document.querySelector('form[class*="catalogue"]') ||
          document.querySelector('.popup form') ||
          document.querySelector('.modal form') ||
          document.querySelector('div[data-section-type="contact-form"] form');

        if (hasCatalogueForm) {
          // Use setTimeout to debounce multiple rapid DOM changes
          setTimeout(() => {
            initCatalogueFormInPopup();
          }, 100);
        }

        // Also check for new catalogue links
        setupCatalogueLinks();
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  // Listen for common events that might indicate form availability
  const formLoadEvents = ['load', 'shopify:section:load'];

  formLoadEvents.forEach(eventName => {
    document.addEventListener(eventName, () => {
      setTimeout(() => {
        initCatalogueFormInPopup();
      }, 500);
    });
  });

  // If this is running in a modal/popup context, listen for modal events
  const modalEvents = ['show.bs.modal', 'shown.bs.modal', 'modalopen', 'popup:open'];

  modalEvents.forEach(eventName => {
    document.addEventListener(eventName, () => {
      setTimeout(() => {
        initCatalogueFormInPopup();
      }, 300);
    });
  });
});
